import {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
  useEffect,
  type ReactNode,
} from "react";
import type {
  ShowcaseFlowConfig,
  OnboardingPersistence,
  OnboardingPersistedState,
  FeatureShowcaseProviderProps,
  UseFeatureShowcaseReturn,
} from "./types";

// ─── Context ────────────────────────────────────────────────────────────────────

interface ShowcaseContextValue extends UseFeatureShowcaseReturn {
  persistence: OnboardingPersistence | null;
  /** Mark the active flow as completed in persistence */
  markCompleted: () => Promise<void>;
  /** Current slide index (controlled externally by the Showcase component) */
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
}

const ShowcaseContext = createContext<ShowcaseContextValue | null>(null);

// ─── Default (noop) persistence ─────────────────────────────────────────────────

const noopPersistence: OnboardingPersistence = {
  load: async () => null,
  save: async () => {},
  clear: async () => {},
};

// ─── Provider ───────────────────────────────────────────────────────────────────

export function FeatureShowcaseProvider({
  children,
  persistence,
}: FeatureShowcaseProviderProps) {
  const [activeFlow, setActiveFlow] = useState<ShowcaseFlowConfig | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const store = persistence ?? noopPersistence;
  const activeFlowRef = useRef<ShowcaseFlowConfig | null>(null);

  const show = useCallback(
    async (config: ShowcaseFlowConfig) => {
      // Check persistence — if already completed/dismissed, don't show
      const persisted = await store.load(config.flowId);
      if (persisted?.completed || persisted?.dismissedAt) {
        return;
      }

      // Resume from persisted index if available
      const startIndex = persisted?.currentSlideIndex ?? 0;

      activeFlowRef.current = config;
      setCurrentIndex(startIndex);
      setActiveFlow(config);
    },
    [store],
  );

  const dismiss = useCallback(async () => {
    const flow = activeFlowRef.current;
    if (flow) {
      await store.save(flow.flowId, {
        currentSlideIndex: currentIndex,
        completed: false,
        dismissedAt: new Date().toISOString(),
      });
      flow.onDismiss?.();
    }
    activeFlowRef.current = null;
    setActiveFlow(null);
    setCurrentIndex(0);
  }, [store, currentIndex]);

  const markCompleted = useCallback(async () => {
    const flow = activeFlowRef.current;
    if (flow) {
      await store.save(flow.flowId, {
        currentSlideIndex: flow.slides.length - 1,
        completed: true,
        completedAt: new Date().toISOString(),
      });
      flow.onComplete?.();
    }
    activeFlowRef.current = null;
    setActiveFlow(null);
    setCurrentIndex(0);
  }, [store]);

  // Debounce slide index persistence to avoid excessive remote writes
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, []);

  const handleSetIndex = useCallback(
    (index: number) => {
      setCurrentIndex(index);

      // Debounce persistence — write after 500ms idle
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        const flow = activeFlowRef.current;
        if (flow) {
          store.save(flow.flowId, {
            currentSlideIndex: index,
            completed: false,
          }).catch((err: unknown) => {
            console.warn("[FeatureShowcase] Failed to persist slide index:", err);
          });
        }
      }, 500);
    },
    [store],
  );

  const value: ShowcaseContextValue = {
    show,
    dismiss,
    isOpen: activeFlow !== null,
    activeFlow,
    persistence: persistence ?? null,
    markCompleted,
    currentIndex,
    setCurrentIndex: handleSetIndex,
  };

  return (
    <ShowcaseContext.Provider value={value}>
      {children}
    </ShowcaseContext.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────────────────────

export function useFeatureShowcase(): UseFeatureShowcaseReturn {
  const ctx = useContext(ShowcaseContext);
  if (!ctx) {
    throw new Error(
      "useFeatureShowcase must be used within a <FeatureShowcaseProvider>",
    );
  }
  return {
    show: ctx.show,
    dismiss: ctx.dismiss,
    isOpen: ctx.isOpen,
    activeFlow: ctx.activeFlow,
  };
}

/** Internal hook — full context including persistence and index control */
export function useShowcaseInternal() {
  const ctx = useContext(ShowcaseContext);
  if (!ctx) {
    throw new Error(
      "useShowcaseInternal must be used within a <FeatureShowcaseProvider>",
    );
  }
  return ctx;
}
